import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { decodeJWTPayload } from '../utils/jwt';

export interface CompanyUser {
  user_id: string | null;
  user_name: string | null;
  email: string | null;
  role: string | null;
  role_key?: string | null;
  company_id: string | null;
  company_name: string | null;
  avatar_url: string | null;
  is_active: boolean | null;
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  is_draft?: boolean;
  draft_id?: string;
  draft_email?: string | null;
  invite_token?: string;
  invite_sent_at?: string | null;
  status?: string;
  subscription_type?: string;
  primary_location_id?: string | null;
  location_name?: string | null;
  created_at?: string | null;
  original_start_date?: string | null;
  last_attendance_date?: string | null;
  contract_start_date?: string | null;
  contract_end_date?: string | null;
  is_child?: boolean | null;
  parent_user_id?: string | null;
  parent_id?: string | null;
  parent_name?: string | null;
  parent_email?: string | null;
  family_id?: string | null;
  is_family_lead?: boolean | null;
}

export interface CompanyUsersResult {
  users: CompanyUser[];
  draftStudents: CompanyUser[];
  draftChildrenCounts: Record<string, number>;
}

interface DraftRecord {
  id: string;
  company_id: string;
  family_id?: string | null;
  is_family_lead?: boolean | null;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  phone_number?: string | null;
  subscription_name?: string | null;
  status?: string | null;
  invite_token?: string | null;
  invite_sent_at?: string | null;
  created_at?: string | null;
  original_start_date?: string | null;
  contract_start_date?: string | null;
  contract_end_date?: string | null;
  company_name?: string | null;
  location_name?: string | null;
  location_id?: string | null;
  companies?: { name: string } | null;
  locations?: { name: string } | null;
}

interface FamilyMember {
  id: string;
  family_id: string;
  company_id?: string;
  is_family_lead?: boolean | null;
  email?: string | null;
  company_name?: string | null;
  location_name?: string | null;
  [key: string]: unknown;
}

/**
 * Hook to fetch company users using get_company_users RPC
 * Validates JWT company_id matches requested company_id - no workaround queries
 * Retries with session refresh if JWT doesn't match, then boots user out if still failing
 */
export function useCompanyUsers(companyId: string | null) {
  return useQuery<CompanyUsersResult>({
    queryKey: ['companyUsers', companyId],
    enabled: !!companyId,
    staleTime: 30_000, // 30 seconds
    retry: (failureCount, error) => {
      // Don't retry if it's a JWT validation error - we'll handle that in queryFn
      if (error instanceof Error && error.message.includes('JWT')) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
    queryFn: async (): Promise<CompanyUsersResult> => {
      if (!companyId) {
        throw new Error('Company ID is required');
      }

      // Validate JWT company_id matches requested company_id (no workaround queries)
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const claims = decodeJWTPayload(session.access_token);
      const jwtCompanyId = claims?.company_id;

      // If JWT doesn't have company_id, try refreshing session once
      if (!jwtCompanyId) {
        console.warn('[useCompanyUsers] JWT missing company_id, refreshing session...');
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) {
          throw new Error('JWT missing company_id - session refresh failed. Please sign in again.');
        }
        // Wait for JWT to propagate
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Check again after refresh
        const { data: { session: newSession } } = await supabase.auth.getSession();
        if (newSession) {
          const newClaims = decodeJWTPayload(newSession.access_token);
          const newJwtCompanyId = newClaims?.company_id;
          if (!newJwtCompanyId) {
            // Boot user out - no workaround queries
            await supabase.auth.signOut();
            throw new Error('JWT missing company_id after refresh - please sign in again.');
          }
        }
      }

      // Validate JWT company_id matches requested company_id (after potential refresh)
      // This ensures RLS policies see the correct company context
      const { data: { session: finalSession } } = await supabase.auth.getSession();
      if (!finalSession?.access_token) {
        throw new Error('No session token available');
      }
      
      const finalClaims = decodeJWTPayload(finalSession.access_token);
      const finalJwtCompanyId = finalClaims?.company_id;
      
      if (!finalJwtCompanyId) {
        // Boot user out - JWT still missing company_id after refresh
        console.error('[useCompanyUsers] JWT still missing company_id after refresh - booting user out');
        await supabase.auth.signOut();
        throw new Error('JWT missing company_id - please sign in again.');
      }
      
      if (finalJwtCompanyId !== companyId) {
        console.error(`[useCompanyUsers] JWT company_id mismatch: JWT has ${finalJwtCompanyId}, requested ${companyId}`);
        throw new Error(`JWT company_id (${finalJwtCompanyId}) does not match requested company_id (${companyId}). Please refresh the page.`);
      }

      // Fetch users using the same RPC as mobile app
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: usersData, error: usersError } = await (supabase as any)
        .rpc('get_company_users', { p_company_id: companyId });

      if (usersError) {
        console.error('[useCompanyUsers] Error fetching users:', usersError);
        
        // If error is due to JWT validation (RLS blocking), boot user out
        if (usersError.message?.includes('JWT') || usersError.message?.includes('permission') || usersError.code === 'PGRST301') {
          console.error('[useCompanyUsers] RLS blocked query - JWT may be invalid. Booting user out.');
          await supabase.auth.signOut();
          throw new Error('Permission denied - please sign in again.');
        }
        
        throw new Error(`Failed to fetch users: ${usersError.message}`);
      }

      // Fetch registered children from child_profiles
      // The get_company_users RPC only returns users from user_profiles/user_company_relationships,
      // but children live in child_profiles (a separate table). We need to fetch them separately
      // so the Children tab filter (is_child === true) works correctly.
      const registeredUserIds = Array.from(
        new Set((usersData || []).map((u: CompanyUser) => u.user_id).filter(Boolean))
      ) as string[];

      // Query 1: children directly assigned to this company
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: companyChildProfilesData, error: companyChildProfilesError } = await (supabase as any)
        .from('child_profiles')
        .select('id, parent_id, first_name, last_name, birth_date, avatar_url, is_active, special_notes, belt_rank, created_at, company_id')
        .eq('is_active', true)
        .eq('company_id', companyId);

      // Query 2: children with no company_id, but whose parent is a registered user in this company
      let parentLinkedChildProfilesData: Array<{ parent_id: string; company_id: string | null }> = [];
      let parentLinkedChildProfilesError: { message?: string } | null = null;

      if (registeredUserIds.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (supabase as any)
          .from('child_profiles')
          .select('id, parent_id, first_name, last_name, birth_date, avatar_url, is_active, special_notes, belt_rank, created_at, company_id')
          .eq('is_active', true)
          .is('company_id', null)
          .in('parent_id', registeredUserIds);

        parentLinkedChildProfilesData = data || [];
        parentLinkedChildProfilesError = error;
      }

      const validChildProfiles = [
        ...(companyChildProfilesData || []),
        ...parentLinkedChildProfilesData,
      ];

      if (companyChildProfilesError || parentLinkedChildProfilesError) {
        console.error(
          '[useCompanyUsers] Error fetching child profiles:',
          companyChildProfilesError || parentLinkedChildProfilesError
        );
      }
      // Fetch draft students from family_leads_view
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: draftDataFromView, error: draftError } = await (supabase as any)
        .from('family_leads_view')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_archived', false)
        .or('user_id.is.null,status.neq.registered');

      if (draftError) {
        console.error('[useCompanyUsers] Error fetching drafts:', draftError);
        // Don't throw - drafts are optional
      }

      // Also fetch ALL draft records directly from staging_import_members
      // This ensures we get parents even if they're not in family_leads_view
      // (e.g., if they have unique UUIDs or don't match the view's criteria)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: allDraftData } = await (supabase as any)
        .from('staging_import_members')
        .select('*, companies!inner(name), locations(name)')
        .eq('company_id', companyId)
        .is('user_id', null)
        .eq('is_archived', false);

      // Combine data from view and direct query, deduplicating by id
      const draftDataMap = new Map<string, DraftRecord>();
      
      // Add records from family_leads_view first
      if (draftDataFromView && draftDataFromView.length > 0) {
        draftDataFromView.forEach((draft: DraftRecord) => {
          draftDataMap.set(draft.id, draft);
        });
      }
      
      // Add records from direct query that aren't already in the map
      // Only add records that are family leads OR don't have a family_id (individuals)
      if (allDraftData && allDraftData.length > 0) {
        allDraftData.forEach((draft: DraftRecord) => {
          if (!draftDataMap.has(draft.id)) {
            // Include if: no family_id (individual), OR is_family_lead = true
            // Do NOT include hasEmail here — non-lead family members with emails
            // would get added as parents and block their child classification
            const isIndividual = !draft.family_id || draft.family_id === '';
            const isFamilyLead = draft.is_family_lead === true;

            if (isIndividual || isFamilyLead) {
              // Add company_name and location_name if not present
              if (!draft.company_name && draft.companies) {
                draft.company_name = draft.companies.name;
              }
              if (!draft.location_name && draft.locations) {
                draft.location_name = draft.locations.name;
              }
              draftDataMap.set(draft.id, draft);
            }
          }
        });
      }
      
      // Fetch all family members separately (they're not all in family_leads_view)
      // Family members are in staging_import_members with the same family_id
      // We group ALL members by family_id, not just those marked as children
      const draftChildrenDataMap = new Map<string, DraftRecord[]>();
      const draftChildrenCountsMap: Record<string, number> = {};
      
      // Get draftData from draftDataMap (will be updated later if needed)
      let draftData = Array.from(draftDataMap.values());
      
      // First, get all unique family_ids from draft parents
      const parentFamilyIds = new Set<string>();
      if (draftData && draftData.length > 0) {
        draftData.forEach((draft) => {
          if (draft.family_id && draft.family_id !== '') {
            parentFamilyIds.add(draft.family_id);
          }
        });
      }
      
      // Fetch ALL family members (not just children) - group by family_id
      // This includes all records with the same family_id, regardless of is_family_lead
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: allFamilyMembersData } = await (supabase as any)
        .from('staging_import_members')
        .select('*')
        .eq('company_id', companyId)
        .is('user_id', null) // Only unregistered
        .eq('is_archived', false)
        .not('family_id', 'is', null)
        .neq('family_id', '');
      
      if (allFamilyMembersData && allFamilyMembersData.length > 0) {
        // Group ALL family members by family_id (not just children)
        const membersByFamily = new Map<string, FamilyMember[]>();
        allFamilyMembersData.forEach((member: FamilyMember) => {
          const familyId = (member.family_id as string) || '';
          if (familyId && familyId !== '') {
            if (!membersByFamily.has(familyId)) {
              membersByFamily.set(familyId, []);
            }
            const familyMembers = membersByFamily.get(familyId);
            if (familyMembers) {
              familyMembers.push(member);
            }
          }
        });
        
        // For each draft parent, find all other family members (excluding the parent itself)
        if (draftData && draftData.length > 0) {
          for (const draft of draftData) {
            if (draft.family_id && draft.family_id !== '' && draft.id) {
              const allFamilyMembers = membersByFamily.get(draft.family_id) || [];
              // Filter out the parent record itself - all others are family members
              const familyMembers = allFamilyMembers.filter((m) => m.id !== draft.id);
              
              draftChildrenCountsMap[draft.id] = familyMembers.length;
              
              if (familyMembers.length > 0) {
                // Convert FamilyMember[] to DraftRecord[] by adding company_id
                const draftFamilyMembers: DraftRecord[] = familyMembers.map(m => ({
                  ...m,
                  company_id: draft.company_id,
                } as DraftRecord));
                draftChildrenDataMap.set(draft.family_id, draftFamilyMembers);
              }
            }
          }
        }
        
        // Also handle families that don't have parents in draftData (from family_leads_view)
        // For each family_id with multiple members, find the lead and group them
        membersByFamily.forEach((members, familyId) => {
          // Only process families with more than one member
          if (members.length > 1) {
            if (!draftChildrenDataMap.has(familyId)) {
              // Find the lead: is_family_lead = true, or first record with email, or first record
              const lead = members.find((m) => (m as FamilyMember).is_family_lead === true) 
                || members.find((m) => (m as FamilyMember).email && (m as FamilyMember).email !== '')
                || members[0];
              
              if (!lead) return;
              
              // All others are family members - convert to DraftRecord with company_id
              const familyMembers: DraftRecord[] = members
                .filter((m) => m.id !== lead.id)
                .map(m => ({
                  ...m,
                  company_id: (m as FamilyMember).company_id || (lead as FamilyMember).company_id || '',
                } as DraftRecord));
              
              if (familyMembers.length > 0) {
                draftChildrenDataMap.set(familyId, familyMembers);
                
                // If the lead isn't in draftDataMap, add it so it shows as a parent
                if (!draftDataMap.has(lead.id)) {
                  const leadRecord: DraftRecord = {
                    ...lead,
                    company_id: (lead as FamilyMember).company_id || '',
                  } as DraftRecord;
                  // Add company_name and location_name if needed
                  if (!leadRecord.company_name) {
                    const firstMember = members.find((m) => (m as FamilyMember).company_name) || members[0];
                    if (firstMember) {
                      leadRecord.company_name = (firstMember as FamilyMember).company_name as string | null;
                      leadRecord.location_name = (firstMember as FamilyMember).location_name as string | null;
                    }
                  }
                  draftDataMap.set(lead.id, leadRecord);
                  
                  // Set count for this parent
                  draftChildrenCountsMap[lead.id] = familyMembers.length;
                } else if (draftDataMap.has(lead.id)) {
                  // Lead is already in draftDataMap, just set the count
                  draftChildrenCountsMap[lead.id] = familyMembers.length;
                }
              }
            }
          }
        });
      }
      
      // Rebuild draftData array from the updated draftDataMap (after all additions)
      draftData = Array.from(draftDataMap.values());

      // Transform draft parents to CompanyUser format
      const draftUsers: CompanyUser[] = (draftData || []).map((draft) => ({
        user_id: null,
        user_name: (draft.first_name && draft.last_name) ? `${draft.first_name} ${draft.last_name}` : null,
        email: draft.email as string | null,
        role: 'draft',
        role_key: 'draft',
        company_id: draft.company_id as string | null,
        company_name: draft.company_name as string | null,
        avatar_url: null,
        is_active: draft.status === 'registered',
        first_name: draft.first_name as string | null,
        last_name: draft.last_name as string | null,
        phone: draft.phone_number as string | null,
        is_draft: true,
        draft_id: draft.id as string,
        draft_email: draft.email as string | null,
        invite_token: draft.invite_token as string | undefined,
        invite_sent_at: draft.invite_sent_at as string | null,
        status: draft.status as string | undefined,
        subscription_type: draft.subscription_name as string | undefined,
        primary_location_id: draft.location_id as string | null,
        location_name: draft.location_name as string | null,
        created_at: draft.created_at as string | null,
        original_start_date: draft.original_start_date as string | null,
        last_attendance_date: null,
        contract_start_date: draft.contract_start_date as string | null,
        contract_end_date: draft.contract_end_date as string | null,
        family_id: draft.family_id as string | null,
        is_family_lead: draft.is_family_lead as boolean | null,
      }));

      // Transform draft children to CompanyUser format and add to users array
      const draftChildrenUsers: CompanyUser[] = [];
      draftChildrenDataMap.forEach((draftChildren, familyId) => {
        // Find the parent draft to get parent info
        const parentDraft = draftData?.find((d) => d.family_id === familyId);
        const parentName = parentDraft
          ? `${parentDraft.first_name || ''} ${parentDraft.last_name || ''}`.trim() || 'Unknown Parent'
          : 'Unknown Parent';
        const parentEmail = parentDraft?.email || null;
        const parentId = parentDraft?.id || null;

        draftChildren.forEach((child) => {
          draftChildrenUsers.push({
            user_id: null,
            user_name: (child.first_name && child.last_name) ? `${child.first_name} ${child.last_name}` : null,
            email: child.email as string | null, // Children may have emails
            role: 'draft',
            role_key: 'draft',
            company_id: child.company_id as string | null,
            company_name: parentDraft?.company_name as string | null,
            avatar_url: null,
            is_active: child.status === 'registered',
            first_name: child.first_name as string | null,
            last_name: child.last_name as string | null,
            phone: child.phone_number as string | null,
            is_draft: true,
            is_child: true, // Mark as child so it appears in children view
            draft_id: child.id as string,
            draft_email: child.email as string | null,
            invite_token: child.invite_token as string | undefined,
            invite_sent_at: child.invite_sent_at as string | null,
            status: child.status as string | undefined,
            subscription_type: child.subscription_name as string | undefined,
            primary_location_id: child.location_id as string | null,
            location_name: child.location_name as string | null,
            created_at: child.created_at as string | null,
            original_start_date: child.original_start_date as string | null,
            last_attendance_date: null,
            contract_start_date: child.contract_start_date as string | null,
            contract_end_date: child.contract_end_date as string | null,
            parent_id: parentId,
            parent_user_id: parentId,
            parent_name: parentName,
            parent_email: parentEmail,
            family_id: child.family_id as string | null,
            is_family_lead: false,
          });
        });
      });

      // Combine regular users, draft parents, and draft children
      // CRITICAL: Deduplicate by draft_id/user_id to prevent double-counting
      const usersMap = new Map<string, CompanyUser>();
      
      // Add registered users first (keyed by user_id)
      if (usersData && usersData.length > 0) {
        usersData.forEach((user: CompanyUser) => {
          if (user.user_id) {
            usersMap.set(user.user_id, {
              user_id: user.user_id,
              user_name: user.user_name,
              email: user.email,
              role: user.role,
              role_key: user.role_key,
              company_id: user.company_id,
              company_name: user.company_name,
              avatar_url: user.avatar_url,
              is_active: user.is_active,
              first_name: user.first_name,
              last_name: user.last_name,
              phone: user.phone,
              is_draft: false,
              primary_location_id: user.primary_location_id,
              location_name: user.location_name,
              created_at: user.created_at,
              original_start_date: user.original_start_date,
              last_attendance_date: user.last_attendance_date,
              contract_start_date: user.contract_start_date,
              contract_end_date: user.contract_end_date,
            });
          }
        });
      }
      
      // Add registered children from child_profiles (keyed by child profile id)
      // These are children who exist in the child_profiles table (not drafts)
      if (validChildProfiles && validChildProfiles.length > 0) {
        // Build a lookup of parent info from registered users
        const parentLookup = new Map<string, CompanyUser>();
        usersMap.forEach((user) => {
          if (user.user_id) {
            parentLookup.set(user.user_id, user);
          }
        });

        validChildProfiles.forEach((child: {
          id: string;
          parent_id: string;
          first_name: string | null;
          last_name: string | null;
          birth_date: string | null;
          avatar_url: string | null;
          is_active: boolean;
          special_notes: string | null;
          belt_rank: string | null;
          created_at: string | null;
          company_id: string | null;
        }) => {
          const parent = parentLookup.get(child.parent_id);
          const childKey = `child_${child.id}`;

          if (!usersMap.has(childKey)) {
            usersMap.set(childKey, {
              user_id: child.id,
              user_name: [child.first_name, child.last_name].filter(Boolean).join(' ') || null,
              email: null, // Children don't have emails
              role: parent?.role || 'Student',
              role_key: parent?.role_key || 'student',
              company_id: child.company_id || parent?.company_id || companyId,
              company_name: parent?.company_name || null,
              avatar_url: child.avatar_url || null,
              is_active: child.is_active,
              first_name: child.first_name,
              last_name: child.last_name,
              phone: null, // Children don't have phone numbers
              is_draft: false,
              is_child: true,
              parent_id: child.parent_id,
              parent_user_id: child.parent_id,
              parent_name: parent?.user_name ?? (
                parent?.first_name || parent?.last_name
                  ? [parent?.first_name, parent?.last_name].filter(Boolean).join(' ')
                  : 'Unknown Parent'
              ),
              parent_email: parent?.email || null,
              primary_location_id: parent?.primary_location_id || null,
              location_name: parent?.location_name || null,
              created_at: child.created_at,
              original_start_date: null,
              last_attendance_date: null,
              contract_start_date: null,
              contract_end_date: null,
            });
          }
        });
      }

      // Add draft parents (keyed by draft_id)
      draftUsers.forEach(draft => {
        if (draft.draft_id && !usersMap.has(draft.draft_id)) {
          usersMap.set(draft.draft_id, draft);
        }
      });
      
      // Add draft children (keyed by draft_id) - only if not already added as parent
      draftChildrenUsers.forEach(child => {
        if (child.draft_id && !usersMap.has(child.draft_id)) {
          usersMap.set(child.draft_id, child);
        }
      });
      
      const allUsers = Array.from(usersMap.values());

      // DEBUG: Log counts to help investigate discrepancy (only in development)
      if (process.env.NODE_ENV === 'development') {
        const registeredCount = usersData?.length || 0;
        const draftParentsCount = draftUsers.length;
        const draftChildrenCount = draftChildrenUsers.length;
        const totalBeforeDedup = registeredCount + draftParentsCount + draftChildrenCount;
        const duplicatesRemoved = totalBeforeDedup - allUsers.length;
        
        // eslint-disable-next-line no-console
        console.log('[useCompanyUsers] DEBUG Counts:', {
          registeredUsers: registeredCount,
          draftParents: draftParentsCount,
          draftChildren: draftChildrenCount,
          totalBeforeDedup,
          totalAfterDedup: allUsers.length,
          duplicatesRemoved,
        });
        
        if (duplicatesRemoved > 0) {
          // eslint-disable-next-line no-console
          console.warn(`[useCompanyUsers] Removed ${duplicatesRemoved} duplicate records`);
        }
      }
      
      // Check for duplicate IDs (should be 0 after deduplication)
      const userIds = new Set<string>();
      const duplicateIds: string[] = [];
      allUsers.forEach(user => {
        const id = user.draft_id || user.user_id || '';
        if (id && userIds.has(id)) {
          duplicateIds.push(id);
        }
        if (id) userIds.add(id);
      });
      
      if (duplicateIds.length > 0) {
        console.warn('[useCompanyUsers] DUPLICATE IDs FOUND AFTER DEDUP:', duplicateIds.slice(0, 10));
      }

      return {
        users: allUsers,
        draftStudents: draftUsers, // Keep draftStudents as just parents for backward compatibility
        draftChildrenCounts: draftChildrenCountsMap,
      };
    },
  });
}
